/* eslint-disable no-param-reassign */
import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { Key } from 'w3c-keys';
import { cloneChildren } from '../_utils';
import { PopoverProps } from './common';

type PopoverPropsOmitted = Omit<PopoverProps, 'targetRef'>;

export const PopoverUtil = (
  targetRef: any,
  styles: any,
  {
    align,
    contentWidth,
    showAlignArrow,
    isVisible,
    content,
    popperRef,
    enableScroll,
    onClickOutside,
    bottomOffset = 0,
    leftOffset = 0,
    className,
    isPositionFixed = false,
    onEscape,
    isReferenceVisible,
    autoFocusOnVisible = false,
    ...restProps
  }: PopoverPropsOmitted
) => {
  const arrowRef = useRef(null);
  const popperRefElement = useRef(null);
  const target = targetRef && targetRef.current ? targetRef.current : targetRef;
  const popper = popperRef && popperRef.current ? popperRef.current : popperRefElement.current;

  const [istargetready, setIsTargetReady] = useState(0);

  const dropdownWidthModifier = useMemo(
    () => ({
      name: 'dropdownWidthModifier',
      enabled: contentWidth !== undefined,
      fn: ({ state }: any) => {
        state.styles.popper.minWidth = `${state.rects.reference.width}px`;

        switch (contentWidth) {
          case 'singleSelectWidth':
            state.styles.popper.maxWidth = '240px';
            break;

          case 'multiSelectWidth':
            state.styles.popper.maxWidth = '320px';
            break;

          case 'singleSelectWithSearchBarWidth':
            state.styles.popper.width = '240px';
            break;

          case 'multiSelectWithSearchBarWidth':
            state.styles.popper.width = '320px';
            break;

          case 'targetWidth':
            state.styles.popper.width = `${state.rects.reference.width}px`;
            break;

          default:
        }
      },
      phase: 'beforeWrite' as any,
      requires: ['computeStyles'],
    }),
    [contentWidth]
  );

  const {
    styles: style,
    attributes,
    update,
  } = usePopper(target, popper, {
    modifiers: [
      { name: 'arrow', options: { element: arrowRef.current } },
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [leftOffset, bottomOffset],
        },
      },
      dropdownWidthModifier,
    ],
    placement: align,
  });

  const updatePopperDebounce = useCallback(
    _.debounce((updatePopper: () => void) => {
      updatePopper();
    }, 50),
    []
  );
  useEffect(() => {
    if (isVisible) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [isVisible]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onEscapeHandler = (event: KeyboardEvent) => {
      if (isVisible && onEscape && event.key === Key.Escape) {
        onEscape(event);
      }
    };
    document.addEventListener('keydown', onEscapeHandler);

    if (autoFocusOnVisible && isVisible) (popperRef || popperRefElement)?.current?.focus();

    return () => {
      document.removeEventListener('keydown', onEscapeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, onEscape]);

  useEffect(() => {
    /**
     * Call "onClickOutside" callback when the popover is open and
     * you click anywhere outside popover and target
     */
    const handleDocumentClick = (event: MouseEvent) => {
      if (onClickOutside && isVisible) {
        const isInsidePopper = popper && popper.contains(event.target);
        const isInsideTarget = target && target.contains(event.target);

        // Note: Must be false (not undefined or null)
        if (isInsidePopper === false && isInsideTarget === false) {
          onClickOutside(event);
        }
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [popper, target, isVisible, onClickOutside]);

  useEffect(() => {
    if (update) {
      updatePopperDebounce(update);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  /* This is used to fix SCAL-78710 where the Action Menu inside popover goes outside the screen. With this extra rerender after target is ready,
       the refs inside Menu component are updated with the correct values. */
  useEffect(() => {
    if (target && target.getBoundingClientRect && !istargetready) {
      setIsTargetReady(1);
    }
  }, [target, istargetready]);

  /* This condition is to check if the window is resized and update the alignment of arrow */
  const updatedArrowAlignment =
    attributes.popper && attributes.popper['data-popper-placement']
      ? attributes.popper['data-popper-placement']
      : align;

  const positioncss = cx(
    className,
    styles.positionable,
    { [styles[updatedArrowAlignment]]: showAlignArrow },
    { [styles.enableScroll]: enableScroll },
    isVisible ? styles.in : styles.out,
    { [styles.positionFixed]: isPositionFixed },
    { [styles.referenceVisible]: isReferenceVisible }
  );

  const ariaProps = {
    'aria-labelledby': restProps['aria-labelledby'],
    'aria-hidden': restProps['aria-hidden'],
    'aria-orientation': restProps['aria-orientation'],
  };

  const { onPointerEnter = _.noop, onPointerLeave = _.noop } =
    restProps as React.HTMLAttributes<HTMLDivElement>;

  const renderPopoverContent = () => (
    <div
      className={positioncss}
      ref={popperRef || popperRefElement}
      style={style.popper as any}
      {...attributes.popper}
      tabIndex={-1}
      data-testid="popover-container"
      {...ariaProps}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {cloneChildren(content, { istargetready })}
      {showAlignArrow && <div ref={arrowRef} style={style.arrow as any} className={styles.arrow} />}
    </div>
  );

  return {
    renderPopoverContent,
  };
};
